import { Transform } from "class-transformer"

export const toBoolean = (value: any) => {
  if (typeof value === "string") {
    return value.trim().toLowerCase() === "true"
  }
  return typeof value === "boolean" ? value : false
}

export const toBooleanOrNull = (value: any) => {
  if (typeof value === "string") {
    return value.trim().toLowerCase() === "true" ? true : value.trim().toLowerCase() === "false" ? false : null
  }
  return typeof value === "boolean" ? value : null
}

export const toBooleanOrUndefined = (value: any) => {
  const parsedValue = toBooleanOrNull(value)
  return parsedValue === null ? undefined : parsedValue
}

/**
 * Converts a string to a Date object or returns null if the string is not a valid date.
 */
export const toDateOrNull = (value: any) => {
  if (typeof value === "string") {
    const trimmedValue = value.trim()
    const dateOnlyPattern = /^\d{4}-\d{2}-\d{2}$/
    if (dateOnlyPattern.test(trimmedValue)) {
      const [year, month, day] = trimmedValue.split("-").map(Number)
      // Set the time to noon UTC to prevent date shifting
      return new Date(Date.UTC(year, month - 1, day, 12))
    } else {
      const parsedValue = new Date(trimmedValue)
      return isNaN(parsedValue.getTime()) ? null : parsedValue
    }
  }
  return null
}

export const toDateOrUndefined = (value: any) => {
  const parsedValue = toDateOrNull(value)
  return parsedValue === null ? undefined : parsedValue
}

export const toDateTimeOrNull = (value: any) => {
  if (typeof value === "string") {
    const trimmedValue = value.trim()
    const parsedValue = new Date(trimmedValue)
    return isNaN(parsedValue.getTime()) ? null : parsedValue
  }
  return null
}

export const toDateTimeOrUndefined = (value: any) => {
  const parsedValue = toDateTimeOrNull(value)
  return parsedValue === null ? undefined : parsedValue
}

export const toDateTimeArrayOrNull = (value: any): Date[] | null => {
  if (Array.isArray(value)) {
    const array = value
      .map((item) => {
        if (typeof item === "string") {
          const trimmedItem = item.trim()
          const parsedItem = new Date(trimmedItem)
          return isNaN(parsedItem.getTime()) ? null : parsedItem
        }
        return null
      })
      .filter((item) => item !== null)

    return array.length > 0 ? array : null
  }
  return toDateTimeArrayOrNull([value])
}

export const toDateTimeArrayOrUndefined = (value: any) => {
  const parsedValue = toDateTimeArrayOrNull(value)
  return parsedValue === null ? undefined : parsedValue
}

export const toFloatOrNull = (value: any) => {
  if (typeof value === "string") {
    const trimmedValue = value.trim()
    const sanitizedValue = trimmedValue.replace(/[^\d.-]/g, "")
    const parsedValue = parseFloat(sanitizedValue)
    return isNaN(parsedValue) ? null : parsedValue
  }
  return typeof value === "number" && !isNaN(value) ? value : null
}

export const toFloatOrUndefined = (value: any) => {
  const parsedValue = toFloatOrNull(value)
  return parsedValue === null ? undefined : parsedValue
}

export const toIntOrNull = (value: any) => {
  if (typeof value === "string") {
    const trimmedValue = value.trim()
    const parsedValue = parseInt(trimmedValue, 10)
    return isNaN(parsedValue) ? null : parsedValue
  }
  return typeof value === "number" && !isNaN(value) ? value : null
}

export const toIntOrUndefined = (value: any) => {
  const parsedValue = toIntOrNull(value)
  return parsedValue === null ? undefined : parsedValue
}

export const toIntArrayOrNull = (value: any) => {
  if (Array.isArray(value)) {
    const array = value
      .map((item) => {
        if (typeof item === "string") {
          const trimmedItem = item.trim()
          const parsedItem = parseInt(trimmedItem, 10)
          return isNaN(parsedItem) ? null : parsedItem
        }
        return typeof item === "number" && !isNaN(item) ? item : null
      })
      .filter((item) => item !== null)

    return array.length > 0 ? array : null
  }
  return null
}

export const toIntArrayOrUndefined = (value: any) => {
  const parsedValue = toIntArrayOrNull(value)
  return parsedValue === null ? undefined : parsedValue
}

export const toIntOrIntArrayOrUndefined = (value: any) => {
  return !Array.isArray(value) ? toIntOrUndefined(value) : toIntArrayOrUndefined(value)
}

export const toTrimmedStringOrNull = (value: any) => {
  if (typeof value === "string") {
    const trimmedValue = value.trim()
    return trimmedValue === "" ? null : trimmedValue
  }
  return null
}

export const toTrimmedStringOrUndefined = (value: any) => {
  const parsedValue = toTrimmedStringOrNull(value)
  return parsedValue === null ? undefined : parsedValue
}

/**
 * Converts the input value to an array of trimmed strings or returns null
 *
 * @param value - The input value to be converted.
 * @returns An array of trimmed strings or null.
 */
export const toTrimmedStringArrayOrNull = (value: any): string[] | null => {
  if (Array.isArray(value)) {
    const array = value
      .map((item) => {
        if (typeof item === "string") {
          const trimmedItem = item.trim()
          return trimmedItem.length > 0 ? trimmedItem : null
        }
        if (typeof item === "number") {
          return !isNaN(item) ? item.toString() : null
        }
        return null
      })
      .filter((item): item is string => item !== null)

    return array.length > 0 ? array : null
  } else if (typeof value === "string") {
    const trimmedValue = value.trim()
    return trimmedValue.length > 0 ? [trimmedValue] : null
  } else if (typeof value === "number") {
    return !isNaN(value) ? [value.toString()] : null
  }
  return null
}

// Custom Transform Decorators
export const TransformToBoolean = () => Transform(({ value }) => toBoolean(value))
export const TransformToBooleanOrNull = () => Transform(({ value }) => toBooleanOrNull(value))
export const TransformToBooleanOrUndefined = () => Transform(({ value }) => toBooleanOrUndefined(value))
export const TransformToDateOrNull = () => Transform(({ value }) => toDateOrNull(value))
export const TransformToDateOrUndefined = () => Transform(({ value }) => toDateOrUndefined(value))
export const TransformToDateTimeOrNull = () => Transform(({ value }) => toDateTimeOrNull(value))
export const TransformToDateTimeOrUndefined = () => Transform(({ value }) => toDateTimeOrUndefined(value))
export const TransformToDateTimeArrayOrNull = () => Transform(({ value }) => toDateTimeArrayOrNull(value))
export const TransformToDateTimeArrayOrUndefined = () => Transform(({ value }) => toDateTimeArrayOrUndefined(value))
export const TransformToFloatOrNull = () => Transform(({ value }) => toFloatOrNull(value))
export const TransformToFloatOrUndefined = () => Transform(({ value }) => toFloatOrUndefined(value))
export const TransformToIntOrNull = () => Transform(({ value }) => toIntOrNull(value))
export const TransformToIntOrUndefined = () => Transform(({ value }) => toIntOrUndefined(value))
export const TransformToIntArrayOrNull = () => Transform(({ value }) => toIntArrayOrNull(value))
export const TransformToIntOrIntArrayOrUndefined = () => Transform(({ value }) => toIntOrIntArrayOrUndefined(value))
export const TransformToTrimmedStringOrNull = () => Transform(({ value }) => toTrimmedStringOrNull(value))
export const TransformToTrimmedStringOrUndefined = () => Transform(({ value }) => toTrimmedStringOrUndefined(value))
export const TransformToTrimmedStringArrayOrNull = () => Transform(({ value }) => toTrimmedStringArrayOrNull(value))
